import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { Header, Footer } from "@/components/Layout";
import { Badge } from "@/components/ui/badge";
import { Clock, ExternalLink, Edit3, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const ACTIVE_COPY = {
  fr: {
    orderLabel: { collective: "Match collectif", individual: "Match individuel" },
    status: { pending: "Validée", in_progress: "En cours" },
    orderedOn: "Commandée le",
    estimatedDelay: "Délai estimé",
    days: "jour(s)",
    tracking: "Suivi de l'analyse",
    steps: [
      { label: "Commande validée", detail: "Votre demande est bien enregistrée." },
      { label: "Analyse en cours", detail: "Mathis travaille sur votre vidéo." },
      { label: "Analyse livrée", detail: "Vous recevrez le lien ici." },
    ],
    request: "Votre demande",
    viewVideo: "Voir la vidéo envoyée",
    editRequest: "Modifier ma demande",
    locked: "Modification verrouillée",
    loadError: "Impossible de charger vos commandes en cours.",
    eyebrow: "Suivi actif",
    title: "Mes commandes en cours",
    subtitle: "Une vue claire de vos commandes validées ou en cours d'analyse, avec timeline et modification tant que l'analyse n'a pas commencé.",
    activeOrders: "Commandes actives",
    notifications: "Notifications",
    toSeeNow: "À voir maintenant",
    markRead: "Marquer comme lu",
    emptyTitle: "Aucune commande en cours",
    emptyText: "Vos commandes actives apparaîtront ici dès validation.",
    backDashboard: "Retour dashboard",
  },
  en: {
    orderLabel: { collective: "Team match", individual: "Individual match" },
    status: { pending: "Confirmed", in_progress: "In progress" },
    orderedOn: "Ordered on",
    estimatedDelay: "Estimated delivery",
    days: "day(s)",
    tracking: "Analysis tracking",
    steps: [
      { label: "Order confirmed", detail: "Your request has been saved." },
      { label: "Analysis in progress", detail: "Mathis is working on your video." },
      { label: "Analysis delivered", detail: "You will receive the link here." },
    ],
    request: "Your request",
    viewVideo: "View submitted video",
    editRequest: "Edit my request",
    locked: "Editing locked",
    loadError: "Unable to load your active orders.",
    eyebrow: "Active tracking",
    title: "My active orders",
    subtitle: "A clear view of confirmed or in-progress orders, with timeline and editing until the analysis has started.",
    activeOrders: "Active orders",
    notifications: "Notifications",
    toSeeNow: "To review now",
    markRead: "Mark as read",
    emptyTitle: "No active order",
    emptyText: "Your active orders will appear here once confirmed.",
    backDashboard: "Back to dashboard",
  },
};

const orderLabel = (type, copy) => copy.orderLabel[type] || copy.orderLabel.individual;

const getRemainingDays = (deadline) => {
  const diff = new Date(deadline) - new Date();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
};

const ActiveOrderCard = ({ order, copy, locale }) => {
  const isPending = order.status === "pending";
  const currentStep = order.status === "in_progress" ? 1 : 0;

  return (
    <article className="rounded-3xl border border-white/10 bg-[#18181B] p-5 md:p-6">
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <Badge className={isPending ? "bg-[#CCFF00]/20 text-[#CCFF00]" : "bg-blue-500/20 text-blue-400"}>
              {isPending ? copy.status.pending : copy.status.in_progress}
            </Badge>
            <Badge className="bg-white/10 text-white">{orderLabel(order.order_type, copy)}</Badge>
          </div>
          <h2 className="text-2xl font-black text-white">Commande #{order.order_id?.slice(0, 8)}</h2>
          <p className="mt-1 text-sm text-zinc-500">{copy.orderedOn} {new Date(order.created_at).toLocaleDateString(locale)}</p>
        </div>

        <div className="rounded-2xl border border-[#CCFF00]/20 bg-[#CCFF00]/10 px-5 py-4">
          <p className="text-xs uppercase text-zinc-500">{copy.estimatedDelay}</p>
          <p className="flex items-center gap-2 text-2xl font-black text-white">
            <Clock className="h-5 w-5 text-[#CCFF00]" />
            {order.estimated_days || Math.max(1, getRemainingDays(order.deadline))} {copy.days}
          </p>
        </div>
      </div>

      <section className="mb-6 rounded-3xl border border-white/10 bg-black/20 p-5">
        <p className="mb-4 text-xs uppercase text-zinc-500">{copy.tracking}</p>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {copy.steps.map((item, index) => (
            <div key={item.label} className={`rounded-2xl border p-4 ${index <= currentStep ? "border-[#CCFF00]/40 bg-[#CCFF00]/10" : "border-white/10 bg-white/[0.03]"}`}>
              <div className="mb-2 flex items-center gap-2">
                <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-black ${index <= currentStep ? "bg-[#CCFF00] text-black" : "bg-white/10 text-zinc-500"}`}>
                  {index + 1}
                </span>
                <p className="font-bold text-white">{item.label}</p>
              </div>
              <p className="text-sm text-zinc-500">{item.detail}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-3xl border border-white/10 bg-[#111111] p-5">
        <p className="mb-2 text-xs uppercase text-zinc-500">{copy.request}</p>
        <p className="whitespace-pre-wrap text-zinc-200">{order.specific_request}</p>

        <div className="mt-5 flex flex-wrap gap-2">
          <a href={order.video_link} target="_blank" rel="noopener noreferrer" className="border border-white/20 text-white px-3 py-2 text-xs font-bold uppercase hover:bg-white/5 transition-colors inline-flex items-center gap-2">
            <ExternalLink className="h-3 w-3" />
            {copy.viewVideo}
          </a>
          {isPending ? (
            <Link to={`/order?order_id=${order.order_id}`} className="bg-[#CCFF00] text-black px-3 py-2 text-xs font-bold uppercase hover:bg-[#AACC00] transition-colors inline-flex items-center gap-2">
              <Edit3 className="h-3 w-3" />
              {copy.editRequest}
            </Link>
          ) : (
            <span className="inline-flex items-center gap-2 border border-blue-500/20 px-3 py-2 text-xs font-bold uppercase text-blue-300">
              <CheckCircle className="h-3 w-3" />
              {copy.locked}
            </span>
          )}
        </div>
      </section>
    </article>
  );
};

const ActiveOrdersPage = () => {
  const { language } = useLanguage();
  const copy = ACTIVE_COPY[language] || ACTIVE_COPY.fr;
  const locale = language === "en" ? "en-US" : "fr-FR";
  const [orders, setOrders] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const [ordersResponse, notificationsResponse] = await Promise.all([
        api.get("/orders"),
        api.get("/notifications")
      ]);
      setOrders(ordersResponse.data);
      setNotifications(notificationsResponse.data);
    } catch {
      toast.error(copy.loadError);
    } finally {
      setLoading(false);
    }
  }, [copy.loadError]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const activeOrders = useMemo(
    () => orders.filter((order) => ["pending", "in_progress"].includes(order.status)).sort((a, b) => {
      const rank = { in_progress: 0, pending: 1 };
      return (rank[a.status] ?? 9) - (rank[b.status] ?? 9) || new Date(b.created_at) - new Date(a.created_at);
    }),
    [orders]
  );

  const activeNotifications = notifications.filter((item) => !item.read).slice(0, 5);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Header />
      <main className="pt-24 pb-16 px-6 md:px-12 lg:px-24">
        <div className="mx-auto max-w-7xl">
          <section className="mb-8 rounded-3xl border border-white/10 bg-[#111111] p-6 md:p-8">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="mb-2 text-sm uppercase tracking-wide text-[#CCFF00]">{copy.eyebrow}</p>
                <h1 className="text-3xl font-black uppercase text-white sm:text-5xl">{copy.title}</h1>
                <p className="mt-3 max-w-2xl text-zinc-400">
                  {copy.subtitle}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3 min-w-full lg:min-w-[360px]">
                <div className="rounded-2xl border border-white/10 bg-black/30 p-4">
                  <p className="text-3xl font-black text-[#CCFF00]">{activeOrders.length}</p>
                  <p className="text-xs uppercase text-zinc-500">{copy.activeOrders}</p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-black/30 p-4">
                  <p className="text-3xl font-black text-white">{activeNotifications.length}</p>
                  <p className="text-xs uppercase text-zinc-500">{copy.notifications}</p>
                </div>
              </div>
            </div>
          </section>

          {activeNotifications.length > 0 && (
            <section className="mb-6 rounded-3xl border border-[#CCFF00]/20 bg-[#CCFF00]/10 p-5">
              <p className="mb-3 text-xs uppercase text-zinc-500">{copy.toSeeNow}</p>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                {activeNotifications.map((item) => (
                  <button
                    key={item.notification_id}
                    onClick={async () => {
                      await api.post(`/notifications/${item.notification_id}/read`, {});
                      setNotifications((current) => current.map((n) => n.notification_id === item.notification_id ? { ...n, read: true } : n));
                    }}
                    className="rounded-2xl border border-white/10 bg-black/20 p-4 text-left hover:bg-black/30"
                  >
                    <p className="font-bold text-white">{item.title}</p>
                    <p className="text-sm text-zinc-400">{item.message}</p>
                    <p className="mt-2 text-xs text-[#CCFF00]">{copy.markRead}</p>
                  </button>
                ))}
              </div>
            </section>
          )}

          {loading ? (
            <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-[#CCFF00] border-t-transparent" />
          ) : activeOrders.length === 0 ? (
            <div className="rounded-3xl border border-white/10 bg-[#18181B] p-10 text-center">
              <CheckCircle className="mx-auto mb-4 h-14 w-14 text-green-400" />
              <p className="mb-2 text-xl font-bold text-white">{copy.emptyTitle}</p>
              <p className="mb-6 text-zinc-500">{copy.emptyText}</p>
              <Link to="/dashboard" className="inline-flex rounded-xl bg-[#CCFF00] px-5 py-3 font-bold uppercase text-black hover:bg-[#AACC00]">
                {copy.backDashboard}
              </Link>
            </div>
          ) : (
            <div className="space-y-6">
              {activeOrders.map((order) => <ActiveOrderCard key={order.order_id} order={order} copy={copy} locale={locale} />)}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ActiveOrdersPage;
